import { createReducer, on } from '@ngrx/store';

import { AlertDtoStoreModel } from '@domain/alerting/alerting.models';
import {
  DashboardOverviewStoreModel,
  DashboardStatsStoreModel,
  EnergyCostStoreModel,
  VehicleStateOfChargeSummaryResponseStoreModel,
} from '@domain/multi-site-dashboard/multi-site-dashboard.models';

import {
  MovableCardsContainerGroupTypeEnum,
  MovableCardsContainerViewModel,
  reconcileCardsLayout,
} from '@shared/components/movable-cards-container';
import { AlertingTableSortPropertyStoreModelEnum } from '@shared/enums/alerting-table-sort-property.enum';
import { SortDirectionStoreModelEnum } from '@shared/enums/sort-direction.enum';
import { StoreToStorageSync } from '@shared/utils/store-to-storage-sync';

import { DEFAULT_ENERGY_COST_RANGE, EnergyCostRange } from '../enums/energy-cost-range.enum';
import { MultiSiteDashboardCard } from '../enums/multi-site-dashboard-card.enum';
import { DEFAULT_VEHICLE_SOC_SUMMARY_RANGE, VehicleSocSummaryRange } from '../enums/vehicle-soc-summary-range.enum';
import {
  clearEnergyCost,
  clearMultiSiteDashboardData,
  clearOverview,
  clearSiteAlerts,
  clearStats,
  clearVehicleStateOfChargeSummary,
  loadEnergyCost,
  loadEnergyCostFailure,
  loadEnergyCostSuccess,
  loadOverview,
  loadOverviewFailure,
  loadOverviewSuccess,
  loadSiteAlerts,
  loadSiteAlertsFailure,
  loadSiteAlertsSuccess,
  loadStats,
  loadStatsFailure,
  loadStatsSuccess,
  loadVehicleStateOfChargeSummary,
  loadVehicleStateOfChargeSummaryFailure,
  loadVehicleStateOfChargeSummarySuccess,
  overviewSocketMessageArrive,
  selectTenant,
  setCardsLayout,
  setEnergyCostRange,
  setReconciledSelectedTenant,
  setSiteAlertsTableSortProperties,
  setVehicleSocSummaryRange,
} from './multi-site-dashboard.actions';

export interface MultiSiteDashboardState {
  selectedTenantId?: string;

  overview?: DashboardOverviewStoreModel;
  overviewIsLoading?: boolean;
  overviewError?: string;

  stats?: DashboardStatsStoreModel;
  statsIsLoading?: boolean;
  statsError?: string;

  siteAlerts?: AlertDtoStoreModel[];
  siteAlertsIsLoading?: boolean;
  siteAlertsError?: string;
  siteAlertsSortProperty: AlertingTableSortPropertyStoreModelEnum;
  siteAlertsSortDirection: SortDirectionStoreModelEnum;

  energyCost?: EnergyCostStoreModel;
  energyCostIsLoading?: boolean;
  energyCostError?: string;
  energyCostRange: EnergyCostRange;

  vehicleSocSummary?: VehicleStateOfChargeSummaryResponseStoreModel;
  vehicleSocSummaryIsLoading?: boolean;
  vehicleSocSummaryError?: string;
  vehicleSocSummaryRange: VehicleSocSummaryRange;

  cardsLayout: MovableCardsContainerViewModel<MultiSiteDashboardCard>;
}

const defaultCardsLayout: MovableCardsContainerViewModel<MultiSiteDashboardCard> = {
  groups: [
    {
      type: MovableCardsContainerGroupTypeEnum.TwoColumns,
      leftCards: [{ key: MultiSiteDashboardCard.SiteFleetsOverview }, { key: MultiSiteDashboardCard.EnergyCostOverview }],
      rightCards: [{ key: MultiSiteDashboardCard.SiteAlerts }, { key: MultiSiteDashboardCard.ChargersOverview }],
    },
    // The Vehicle State of Charge Summary is a full-width (double, non-shrinking) card pinned to the
    // bottom by default, so it lives in its own single-column group.
    {
      type: MovableCardsContainerGroupTypeEnum.SingleColumn,
      cards: [{ key: MultiSiteDashboardCard.VehicleStateOfChargeSummary }],
    },
  ],
};

export const initialState: MultiSiteDashboardState = {
  cardsLayout: reconcileCardsLayout(StoreToStorageSync.getStorageData()?.multiSiteDashboardCardsLayout, defaultCardsLayout),
  siteAlertsSortProperty: AlertingTableSortPropertyStoreModelEnum.Timestamp,
  siteAlertsSortDirection: SortDirectionStoreModelEnum.Desc,
  energyCostRange: StoreToStorageSync.getMultiSiteDashboardEnergyCostRange() ?? DEFAULT_ENERGY_COST_RANGE,
  vehicleSocSummaryRange: StoreToStorageSync.getMultiSiteDashboardVehicleSocSummaryRange() ?? DEFAULT_VEHICLE_SOC_SUMMARY_RANGE,
};

export const multiSiteDashboardFeatureKey = 'multiSiteDashboard';

export const multiSiteDashboardReducer = createReducer(
  initialState,

  on(selectTenant, (state, { tenantId }): MultiSiteDashboardState => ({ ...state, selectedTenantId: tenantId })),
  on(setReconciledSelectedTenant, (state, { tenantId }): MultiSiteDashboardState => ({ ...state, selectedTenantId: tenantId })),

  on(loadOverview, (state): MultiSiteDashboardState => ({ ...state, overviewIsLoading: true, overviewError: undefined })),
  on(loadOverviewSuccess, (state, { overview }): MultiSiteDashboardState => ({ ...state, overviewIsLoading: false, overview })),
  on(loadOverviewFailure, (state, { error }): MultiSiteDashboardState => ({ ...state, overviewIsLoading: false, overviewError: error })),
  on(overviewSocketMessageArrive, (state, { overview }): MultiSiteDashboardState => ({ ...state, overview })),
  on(clearOverview, (state): MultiSiteDashboardState => ({ ...state, overview: undefined, overviewIsLoading: undefined })),

  on(loadStats, (state): MultiSiteDashboardState => ({ ...state, statsIsLoading: true, statsError: undefined })),
  on(loadStatsSuccess, (state, { stats }): MultiSiteDashboardState => ({ ...state, statsIsLoading: false, stats })),
  on(loadStatsFailure, (state, { error }): MultiSiteDashboardState => ({ ...state, statsIsLoading: false, statsError: error })),
  on(clearStats, (state): MultiSiteDashboardState => ({ ...state, stats: undefined, statsIsLoading: undefined })),

  on(
    loadSiteAlerts,
    (state, { polling }): MultiSiteDashboardState => ({
      ...state,
      siteAlertsIsLoading: true,
      siteAlertsError: undefined,
      siteAlerts: polling ? state.siteAlerts : undefined,
    })
  ),
  on(loadSiteAlertsSuccess, (state, { alerts }): MultiSiteDashboardState => ({ ...state, siteAlertsIsLoading: false, siteAlerts: alerts })),
  on(
    loadSiteAlertsFailure,
    (state, { error }): MultiSiteDashboardState => ({ ...state, siteAlertsIsLoading: false, siteAlertsError: error })
  ),
  on(clearSiteAlerts, (state): MultiSiteDashboardState => ({ ...state, siteAlerts: undefined, siteAlertsIsLoading: undefined })),

  on(
    setSiteAlertsTableSortProperties,
    (state, { sortProperty, sortDirection }): MultiSiteDashboardState => ({
      ...state,
      siteAlertsSortProperty: sortProperty,
      siteAlertsSortDirection: sortDirection,
    })
  ),

  on(
    loadEnergyCost,
    (state): MultiSiteDashboardState => ({ ...state, energyCostIsLoading: true, energyCostError: undefined, energyCost: undefined })
  ),
  on(loadEnergyCostSuccess, (state, { energyCost }): MultiSiteDashboardState => ({ ...state, energyCostIsLoading: false, energyCost })),
  on(
    loadEnergyCostFailure,
    (state, { error }): MultiSiteDashboardState => ({ ...state, energyCostIsLoading: false, energyCostError: error })
  ),
  on(setEnergyCostRange, (state, { range }): MultiSiteDashboardState => ({ ...state, energyCostRange: range })),
  on(clearEnergyCost, (state): MultiSiteDashboardState => ({ ...state, energyCost: undefined, energyCostIsLoading: undefined })),

  on(
    loadVehicleStateOfChargeSummary,
    (state): MultiSiteDashboardState => ({
      ...state,
      vehicleSocSummaryIsLoading: true,
      vehicleSocSummaryError: undefined,
      vehicleSocSummary: undefined,
    })
  ),
  on(
    loadVehicleStateOfChargeSummarySuccess,
    (state, { summary }): MultiSiteDashboardState => ({ ...state, vehicleSocSummaryIsLoading: false, vehicleSocSummary: summary })
  ),
  on(
    loadVehicleStateOfChargeSummaryFailure,
    (state, { error }): MultiSiteDashboardState => ({ ...state, vehicleSocSummaryIsLoading: false, vehicleSocSummaryError: error })
  ),
  on(setVehicleSocSummaryRange, (state, { range }): MultiSiteDashboardState => ({ ...state, vehicleSocSummaryRange: range })),
  on(
    clearVehicleStateOfChargeSummary,
    (state): MultiSiteDashboardState => ({ ...state, vehicleSocSummary: undefined, vehicleSocSummaryIsLoading: undefined })
  ),

  on(setCardsLayout, (state, { layout }): MultiSiteDashboardState => ({ ...state, cardsLayout: layout })),

  // Preserve dashboard-specific user state across clears (e.g. on screen leave).
  on(
    clearMultiSiteDashboardData,
    (state): MultiSiteDashboardState => ({
      ...initialState,
      selectedTenantId: state.selectedTenantId,
      siteAlertsSortProperty: state.siteAlertsSortProperty,
      siteAlertsSortDirection: state.siteAlertsSortDirection,
      energyCostRange: state.energyCostRange,
      vehicleSocSummaryRange: state.vehicleSocSummaryRange,
      cardsLayout: state.cardsLayout,
    })
  )
);
